import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { getResponsiveValue } from '../../styles/breakpoints';
import { useUser } from '../../components/LoginPersistence';

const DEFAULT_PROFILE_PHOTOS = [
  require('../../../assets/profile_photos/koi.png'),
  require('../../../assets/profile_photos/bear.png'),
];

export function MessageInput({ onSubmit }) {
  const [message, setMessage] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const { user: userData } = useUser();
  const auth = getAuth();

  const getRandomDefaultPhoto = () => {
    const randomIndex = Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length);
    return DEFAULT_PROFILE_PHOTOS[randomIndex];
  };

  const handleSubmit = (e, isAnonymous = false) => {
    e.preventDefault();
    if (message.trim() || photoFile) {
      onSubmit(message.trim(), photoFile, isAnonymous);
      setMessage('');
      setPhotoFile(null);
      setPhotoPreview(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <InputContainer>
      {/* <UserAvatar 
        src={userData?.photoURL || getRandomDefaultPhoto()} 
        alt="User avatar"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = getRandomDefaultPhoto();
        }}
      /> */}
      <Form onSubmit={handleSubmit}>
        {photoPreview && (
          <PhotoPreviewContainer>
            <PhotoPreview src={photoPreview} alt="Photo preview" />
            <RemovePhotoButton type="button" onClick={handleRemovePhoto}>×</RemovePhotoButton>
          </PhotoPreviewContainer>
        )}
        <InputWrapper>
          <InputField
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="type your message..."
            placeholderFontFamily="Computer Says No"
          />
          <PhotoUploadButton 
            type="button" 
            onClick={() => fileInputRef.current.click()}
            title="Add a photo"
          >
            📷
          </PhotoUploadButton>
          <FileInput 
            type="file" 
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
          />
        </InputWrapper>
        <ButtonContainer>
          <SubmitButton type="submit" onClick={(e) => handleSubmit(e, false)} disabled={!message.trim() && !photoFile}>
            Send
          </SubmitButton>
          <AnonymousButton type="button" onClick={(e) => handleSubmit(e, true)} disabled={!message.trim() && !photoFile}>
            Post Anonymously
          </AnonymousButton>
        </ButtonContainer>
      </Form>
    </InputContainer>
  );
}

const InputContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${getResponsiveValue({
    mobile: '10px',
    tablet: '15px',
    laptop: '15px',
    desktop: '15px'
  })};
  width: ${getResponsiveValue({
    mobile: '95%',
    tablet: '90%',
    laptop: '100%',
    desktop: '100%'
  })};
  max-width: ${getResponsiveValue({
    mobile: '95%',
    tablet: '90%',
     laptop: '100%',
    desktop: '100%'
  })};
  padding: ${getResponsiveValue({
    mobile: '15px',
    tablet: '10px',
    laptop: '10px',
    desktop: '10px'
  })};
  margin-left: '0px'
  background-color: transparent;
  border-radius: 10px;
  box-shadow: none;
`;

const UserAvatar = styled.img`
  width: ${getResponsiveValue({
    mobile: '32px',
    tablet: '40px',
    laptop: '40px',
    desktop: '40px'
  })};
  height: ${getResponsiveValue({
    mobile: '32px',
    tablet: '40px',
    laptop: '40px',
    desktop: '40px'
  })};
  border-radius: 50%;
  object-fit: cover;
  background-color: rgba(255, 255, 255, 0.1);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;

const InputField = styled.input`
  flex: 1;
  border: 4px solid #424252;
  background-color: #e9e9e8;
  border-radius: 30px;
  padding: 10px 15px;
  padding-right: 40px;
  color: '#000000';
  font-family: 'Computer Says No';
  font-size: ${getResponsiveValue({
    mobile: '22px',
    tablet: '26px',
    laptop: '26px',
    desktop: '26px'
  })};
  line-height: 1.4;

  &::placeholder {
    color: '#000000';
  }

  &:focus {
    outline: none;
    background-color: '#e4e4e4';
    border-color: #424252;
  }
`;

const PhotoUploadButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
  transition: color 0.3s ease;

  &:hover {
    color: white;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const PhotoPreviewContainer = styled.div`
  position: relative;
  margin-bottom: 10px;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
`;

const PhotoPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const RemovePhotoButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: row;
  width: 100%;
`;

const SubmitButton = styled.button`
  background-color: #424252;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Computer Says No';
  font-size: ${getResponsiveValue({
    mobile: '18px',
    tablet: '22px',
    laptop: '22px',
    desktop: '22px'
  })};
  transition: background-color 0.3s ease;
  flex: 1;

  &:hover {
    background-color: #333333;
  }

  &:disabled {
    background-color: #424252;
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const AnonymousButton = styled.button`
  background-color: #666666;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Computer Says No';
  font-size: ${getResponsiveValue({
    mobile: '18px',
    tablet: '22px',
    laptop: '22px',
    desktop: '22px'
  })};
  transition: background-color 0.3s ease;
  flex: 1;

  &:hover {
    background-color: #555555;
  }

  &:disabled {
    background-color: #666666;
    cursor: not-allowed;
    opacity: 0.5;
  }
`; 